/**
 * On-device insurance-card OCR — tesseract.js (WASM) in the patient's browser.
 *
 * Privacy: the card image is processed entirely on the patient's device; it
 * never reaches any server unencrypted. (tesseract.js lazily fetches its WASM
 * engine + English model from a CDN — public engine assets, no PHI outbound.)
 *
 * The extraction heuristics mirror the Mac-side insurance_card_ocr.py so the
 * instant on-device prefill and the physician-side OCR chips agree with each
 * other. Both are conservative: candidates cap at 0.92 confidence and the
 * patient/physician always confirms.
 *
 * Carrier detection + per-carrier printed-label regexes are DATA-DRIVEN from
 * the generated insurerTerminology.json (exported from insurer_registry — the
 * single source both OCR implementations share). A tiny hardcoded carrier list
 * is kept ONLY as a fallback for when the JSON import fails.
 *
 * Returns candidates shaped { field, value, confidence, level, concept } where:
 *   - field   : legacy names kept for wire-compat —
 *               insurance_provider | insurance_id | plan_number | group_id
 *   - level   : canonical attribution level — "person" | "plan" | ""
 *   - concept : canonical concept — member_id | plan_id | division_id |
 *               plan_sponsor | ""
 * The additive level/concept let PatientConfirmDetails route candidates by
 * identifier level instead of guessing (kills the group_id→plan_number
 * collapse). `field` stays legacy so old readers keep working.
 */

import terminology from "./insurerTerminology.json";

/** One mined OCR candidate. `field` is a legacy wire name; level/concept are
 *  the canonical attribution tags. */
interface Candidate {
  field: string;
  value: string;
  confidence: number;
  level: string;
  concept: string;
}

/** Per-carrier terminology entry from insurerTerminology.json (messy shape). */
type TerminologyEntry = Record<string, any>;

// concept → [legacy field name, level] (wire-compat + attribution).
const CONCEPT_TO_FIELD: Record<string, [string, string]> = {
  member_id: ["insurance_id", "person"],
  plan_id: ["plan_number", "plan"],
  division_id: ["group_id", "plan"],
  plan_sponsor: ["plan_number", "plan"],
};

// Tiny hardcoded fallback carrier list — used ONLY if the terminology JSON is
// missing/unparseable. No terminology (labels/levels) is available in this
// path, so candidates fall back to the generic first-pass tags.
const FALLBACK_CARRIER_PATTERNS: [RegExp, string][] = [
  [/medavie\s+blue\s+cross/i, "Medavie Blue Cross"],
  [/pacific\s+blue\s+cross/i, "Pacific Blue Cross"],
  [/blue\s+cross/i, "Blue Cross"],
  [/sun\s*life/i, "Sun Life"],
  [/manulife/i, "Manulife"],
  [/great.?west\s*life/i, "Great-West Life"],
  [/canada\s+life/i, "Canada Life"],
  [/green\s+shield/i, "Green Shield Canada"],
  [/desjardins/i, "Desjardins"],
];

/**
 * Build carrier detection patterns + per-carrier terminology from the imported
 * terminology JSON. Detection patterns are already regex strings from the
 * exporter, so `new RegExp(pattern, "i")` compiles them directly; a bad pattern
 * is skipped rather than thrown. Falls back to the hardcoded list on any error.
 */
function buildCarrierIndex(): {
  patterns: [RegExp, string][];
  termByName: Record<string, TerminologyEntry>;
  fromTerminology: boolean;
} {
  try {
    const insurers = ((terminology as any) && (terminology as any).insurers) || {};
    const patterns: [RegExp, string][] = [];
    const termByName: Record<string, TerminologyEntry> = {};
    for (const [name, entry] of Object.entries(insurers) as [string, TerminologyEntry][]) {
      termByName[name] = entry;
      const rawPatterns = (entry && entry.detection_patterns) || [];
      for (const p of rawPatterns) {
        try {
          patterns.push([new RegExp(p, "i"), name]);
        } catch {
          /* skip an un-compilable pattern; other patterns still register */
        }
      }
    }
    if (patterns.length === 0) throw new Error("no carrier patterns in terminology");
    return { patterns, termByName, fromTerminology: true };
  } catch {
    return {
      patterns: FALLBACK_CARRIER_PATTERNS,
      termByName: {},
      fromTerminology: false,
    };
  }
}

// The bare `id` alternative is word-fenced and swallows its own qualifier
// word. Without the fence "Id" matched the first two letters of the printed
// label "Identification Number" and the value token that followed was
// "entification Number" — proposed to the patient as their member ID
// (imp_c43a2a67). Mirrors _MEMBER_ID_LABELS in insurance_card_ocr.py.
const MEMBER_ID_LABELS =
  /(?:member\s+(?:i\.?d\.?|id\.?|no\.?|number)|\bid\b\s*(?:no\.?|number|num\.?|#)?\s*[:#]?|certificate\s+(?:no\.?|num\.?|number|#)|cert\.?\s+(?:no\.?|number)|participant\s+(?:no\.?|number)|identification\s+(?:no\.?|number)|beneficiary\s+id|subscriber\s+id)/gi;

// Plan/policy/contract labels. The group-plan overlap was REMOVED (it stole
// "Group No:" lines from GROUP_LABELS) — group numbers are owned by
// GROUP_LABELS and re-tagged plan-vs-division by the carrier pass.
const PLAN_NUMBER_LABELS =
  /(?:plan\s+(?:no\.?|number|#)|policy\s+(?:no\.?|number|#)|policy\s*[:#]?|contract\s+(?:no\.?|number|#)|plan\s*[:#]?)/gi;

const GROUP_LABELS =
  /(?:group\s+(?:no\.?|number|#|id)|group\s*[:#]|employer\s+(?:no\.?|number|group)|division\s+(?:no\.?|number))/gi;

const ALPHANUM_TOKEN = /([A-Z0-9][A-Z0-9\- ]{3,19})/i;

// Trailing qualifier words on a printed label are treated as optional +
// interchangeable so OCR abbreviations match ("Plan contract number" also
// matches "Plan contract no"; bare "Certificate" matches "Certificate number").
const LABEL_QUALIFIER_RE = /\s*(?:number|no\.?|num\.?|#|id)\s*$/i;

function escapeRegExp(s: unknown) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const IS_ALNUM = /[A-Za-z0-9]/;

/** Compile a printed-label regex from a carrier's label + aliases.
 *
 * Alphanumeric stem edges are fenced with `\b`: Medavie's "ID number" label
 * reduces to the stem "ID", which unfenced matched inside "Identification"
 * (imp_c43a2a67). Per-edge, because a stem may end in punctuation ("No."),
 * where `\b` would mean the opposite. Mirrors _compile_label_regex in
 * insurance_card_ocr.py. */
function compileLabelRegex(labels: unknown[]): RegExp | null {
  const stems: string[] = [];
  for (const l of labels) {
    if (!l || !String(l).trim()) continue;
    const stem = String(l).trim().replace(LABEL_QUALIFIER_RE, "").trim();
    if (stem) stems.push(stem);
  }
  if (stems.length === 0) return null;
  stems.sort((a, b) => b.length - a.length); // longest stem wins
  const body = stems
    .map(
      (s) =>
        (IS_ALNUM.test(s[0]) ? "\\b" : "") +
        escapeRegExp(s) +
        (IS_ALNUM.test(s[s.length - 1]) ? "\\b" : "")
    )
    .join("|");
  return new RegExp(`(?:${body})\\s*(?:no\\.?|number|num\\.?|#|id)?\\s*[:#]?`, "gi");
}

function extractAfterLabel(
  text: string,
  labelRe: RegExp,
  fieldName: string,
  baseConf: number,
  level: string,
  concept: string
): Candidate[] {
  const out: Candidate[] = [];
  labelRe.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = labelRe.exec(text)) !== null) {
    const after = text.slice(m.index + m[0].length, m.index + m[0].length + 60);
    const valM = ALPHANUM_TOKEN.exec(after);
    if (m[0].length === 0) labelRe.lastIndex += 1; // guard against zero-width loops
    if (!valM) continue;
    let val = (valM[1] || "").trim().replace(/^[\s-]+|[\s-]+$/g, "");
    if (!val || val.length < 4) continue;
    let conf = baseConf;
    if (val.length >= 6) conf = Math.min(0.92, conf + 0.05);
    out.push({
      field: fieldName,
      value: val,
      confidence: Math.round(conf * 100) / 100,
      level: level || "",
      concept: concept || "",
    });
  }
  return out;
}

/** Resolve a detected carrier's terminology entry (name or alias match). */
function resolveCarrierTerminology(
  termByName: Record<string, TerminologyEntry>,
  carrierName: string | null
): TerminologyEntry | null {
  if (!carrierName) return null;
  if (termByName[carrierName]) return termByName[carrierName];
  const lc = carrierName.trim().toLowerCase();
  for (const [name, entry] of Object.entries(termByName)) {
    if (name.trim().toLowerCase() === lc) return entry;
    const aliases = (entry.aliases || []).map((a: unknown) => String(a).trim().toLowerCase());
    if (aliases.includes(lc)) return entry;
  }
  return null;
}

/**
 * Carrier-aware second pass (runs AFTER carrier detection). Mirrors
 * _apply_carrier_pass on the Mac:
 *   1. exact printed-label + alias candidates at 0.85, tagged by level/concept;
 *   2. format_regex soft modifier (+0.05 match / −0.15 mismatch, never discard);
 *   3. re-tag first-pass group candidates → plan when carrier has no division.
 */
function applyCarrierPass(
  text: string,
  termByName: Record<string, TerminologyEntry>,
  carrierName: string | null,
  candidates: Candidate[]
): Candidate[] {
  const entry = resolveCarrierTerminology(termByName, carrierName);
  if (!entry) return candidates;

  const declaresDivision = !!entry.declares_division;
  const specs = entry.id_terminology || [];

  for (const spec of specs) {
    const target = CONCEPT_TO_FIELD[spec.concept];
    if (!target) continue;
    const [fieldName, level] = target;
    const labels = [spec.label].concat(spec.aliases || []);
    const labelRe = compileLabelRegex(labels);
    if (!labelRe) continue;
    const found = extractAfterLabel(text, labelRe, fieldName, 0.85, level, spec.concept);

    let fmtRe: RegExp | null = null;
    if (spec.format_regex) {
      try {
        fmtRe = new RegExp(spec.format_regex);
      } catch {
        fmtRe = null;
      }
    }
    if (fmtRe) {
      for (const c of found) {
        if (fmtRe.test(c.value)) {
          c.confidence = Math.round(Math.min(0.92, c.confidence + 0.05) * 100) / 100;
        } else {
          c.confidence = Math.round(Math.max(0, c.confidence - 0.15) * 100) / 100;
        }
      }
    }
    candidates.push(...found);
  }

  if (!declaresDivision) {
    for (const c of candidates) {
      if (c.field === "group_id" && (c.concept === "" || c.concept === "division_id")) {
        c.field = "plan_number";
        c.concept = "plan_id";
        c.level = "plan";
      }
    }
  }

  return candidates;
}

/** Mine OCR text — mirrors extract_candidates_from_text on the Mac. */
export function mineCardText(text: string): Candidate[] {
  const { patterns, termByName } = buildCarrierIndex();
  const candidates: Candidate[] = [];

  // 1. Carrier detection — prefer the MOST SPECIFIC carrier (longest matched
  //    span), so "Medavie Blue Cross" beats a generic "Blue Cross" match.
  let detectedCarrier: string | null = null;
  let best: [number, string] | null = null; // [len, name]
  for (const [pattern, canonical] of patterns) {
    const m = pattern.exec(text);
    pattern.lastIndex = 0;
    if (m) {
      const span = m[0].length;
      if (best === null || span > best[0]) best = [span, canonical];
    }
  }
  if (best) {
    detectedCarrier = best[1];
    candidates.push({
      field: "insurance_provider",
      value: detectedCarrier,
      confidence: 0.88,
      level: "",
      concept: "",
    });
  }

  // 2. Generic first pass — tagged by canonical level/concept.
  candidates.push(
    ...extractAfterLabel(text, MEMBER_ID_LABELS, "insurance_id", 0.75, "person", "member_id")
  );
  candidates.push(
    ...extractAfterLabel(text, PLAN_NUMBER_LABELS, "plan_number", 0.75, "plan", "plan_id")
  );
  candidates.push(
    ...extractAfterLabel(text, GROUP_LABELS, "group_id", 0.7, "plan", "division_id")
  );

  // 2b. Carrier-aware second pass.
  applyCarrierPass(text, termByName, detectedCarrier, candidates);

  // Highest-confidence candidate per (field, value) pair.
  const best2: Record<string, Candidate> = {};
  for (const c of candidates) {
    const key = `${c.field}::${String(c.value).toUpperCase()}`;
    if (!best2[key] || c.confidence > best2[key].confidence) best2[key] = c;
  }
  return Object.values(best2);
}

/**
 * OCR an image File/Blob on-device and return { candidates, text }.
 * Never throws — OCR is best-effort sugar on top of manual entry; on any
 * failure it resolves to no candidates and the patient just types.
 */
export async function extractCardFields(file: File | Blob) {
  try {
    const Tesseract = (await import("tesseract.js")).default as any;
    const { data } = await Tesseract.recognize(file, "eng");
    const text = (data && data.text) || "";
    return { candidates: mineCardText(text), text };
  } catch {
    return { candidates: [], text: "" };
  }
}
